// lib/classAdminApi.js
// R9 수업 추가·수정·삭제와 운동 프로그램 관리자 API. 계약: docs/architecture/record-daily-design.md 0절 7·9·10·12·14
//   POST   /v1/admin/classes                      { date, type, title?, start_time, end_time, capacity } → 201 수업
//          title = 기타(other) 수업 이름(선택, 2026-10-24 사장님 결정 B). 기타일 때만 보내고 서버도 기타일 때만 읽는다(비면 "기타",
//          20자까지, 마크업·제어 문자·다른 타입 이름이면 422 COMMON_006 details.field "title"). 다른 타입은 이름 = 타입 표시 이름
//   PUT    /v1/admin/classes/{class_id}           같은 본문 → 200, 409 CLASS_HAS_BOOKINGS · CLASS_HAS_WORKOUTS(서버 구현: 기록 있는 수업도 못 고침.
//          이름도 첫 예약 전에만 바꿀 수 있다)
//   DELETE /v1/admin/classes/{class_id}           → 204, 409 CLASS_HAS_BOOKINGS · CLASS_HAS_WORKOUTS
//   POST·PUT 409 CLASS_TIME_CONFLICT: 같은 날짜·시작 시각에 다른 수업이 있음(R9 리뷰 1차. 서버 문구 끝 괄호에 그 수업 시각·이름)
//   DELETE /v1/admin/classes?date=YYYY-MM-DD       → 200 { deleted: n }(n은 0일 수 있음). CAR-130 날짜 닫기(2026-10-24 기본값 D2, 휴무일).
//          한 트랜잭션에서 그 날짜 수업을 모두 잠그고 하나라도 예약 행(취소 포함)이나 운동 기록이 있으면 아무것도 지우지 않고
//          409 CLASS_HAS_BOOKINGS · CLASS_HAS_WORKOUTS(문구에 막힌 수업의 시각·이름, details = { date, blocked: [{ class_id,
//          start_time "HH:MM", name, reason "bookings" | "workouts" }] }). 날짜가 없거나 틀리면 422 COMMON_006(details.field "date").
//          예약 취소·횟수 반환은 하지 않는다(R9 "수업 취소는 만들지 않는다", record-daily-design.md 0절 9)
//   GET    /v1/admin/classes/{class_id}/workouts/exists → { exists }
//   GET    /v1/admin/classes/{class_id}/bookings/exists → { exists } (취소 포함 예약 행. 수정·삭제 409 CLASS_HAS_BOOKINGS 기준)
//   GET    /v1/admin/classes/schedule?date=       → { classes } (운동 프로그램 탭의 "아직 안 씀" 표시용)
//   GET    /v1/admin/daily-programs?from=&to=     → { items: [{ date, type, type_label, headline, content, cardio_focus, weight_focus, created_at, updated_at }] }
//          (R10: cardio_focus·weight_focus는 값 또는 null. R9 focus 키는 없다)
//   GET    /v1/admin/daily-programs/{date}/{type} → 항목, 없으면 404
//   PUT    /v1/admin/daily-programs/{date}/{type} { headline, content, cardio_focus(필수), weight_focus(null = 고르지 않음) } → 200, 처음 저장이면 201
//          (R10: 본문에 R9 focus 키가 있으면 422 COMMON_006 details.field "focus". 422 이유는 details.reason 글자를 그대로 보인다)
//          (응답 = 항목 + summaries_regenerated: 이번 저장에서 다시 만든 오늘의 총평 수, 고침이면 0. R9 리뷰 4차)
// api.js의 api/apiJson은 실패 응답마다 전역 에러 팝업을 띄우고 본문을 읽어 버린다. 이 화면들은 오류 코드마다 문구가 달라서
// (409 예약 있음·기록 있음) API 주소 규칙은 api.js와 같게 두고 직접 fetch한다(쿠키 동봉, lib/rewardsApi.js와 같은 방식).
// 이 파일은 import가 없다(scripts/test-class-info.mjs가 node로 그대로 불러 시험한다).

const API_BASE =
  (typeof window !== "undefined" && window.__APP_CONFIG__?.API_BASE) ||
  process.env.VUE_APP_API_BASE || // Vue CLI
  ""; // 같은 오리진이면 빈 문자열

// status 0 = 응답을 받지 못함(네트워크 오류). serverMessage는 서버가 준 문구(있으면), code는 서버 오류 코드,
// field는 422 입력 검사 실패의 칸(details.field, 없으면 ""), details는 서버 error.details 객체(없으면 null. 날짜 닫기 409의 막힌 수업 목록)
export class ClassAdminApiError extends Error {
  constructor(
    status,
    serverMessage = "",
    code = "",
    field = "",
    details = null
  ) {
    super(serverMessage || (status ? `HTTP ${status}` : "NETWORK"));
    this.name = "ClassAdminApiError";
    this.status = status;
    this.serverMessage = serverMessage;
    this.code = code;
    this.field = field;
    this.details = details;
  }
}

// 실패 응답 본문에서 문구·코드를 꺼낸다.
// {"success":false,"error":{"code","message","details":{"field","reason"}}} / {"error":"…"} / {"message":"…","code":"…"} / 일반 텍스트.
// 422 입력 검사 실패는 message가 "입력값이 올바르지 않습니다"뿐이고 칸별 이유는 details.reason에 있어 그것을 먼저 쓴다.
// 칸 이름(details.field)은 field로 돌려준다(서버 문구가 한국어가 아닐 때 칸에 맞는 안내를 고른다).
// details 객체는 그대로 돌려준다(객체가 아니면 null)
async function readErrorBody(res) {
  let text = "";
  try {
    text = await res.text();
  } catch {
    return { message: "", code: "", field: "", details: null };
  }
  try {
    const data = JSON.parse(text);
    if (data?.error && typeof data.error === "object") {
      const raw = data.error.details;
      const details =
        raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
      const reason = details?.reason;
      const field = details?.field;
      return {
        message: String(
          (typeof reason === "string" && reason.trim()) ||
            data.error.message ||
            ""
        ),
        code: String(data.error.code || ""),
        field: typeof field === "string" ? field : "",
        details,
      };
    }
    if (typeof data?.error === "string") {
      return {
        message: data.error,
        code: String(data.code || ""),
        field: "",
        details: null,
      };
    }
    if (typeof data?.message === "string") {
      return {
        message: data.message,
        code: String(data.code || ""),
        field: "",
        details: null,
      };
    }
  } catch {
    // JSON이 아니면 아래에서 텍스트 그대로
  }
  return {
    message: String(text || "").trim(),
    code: "",
    field: "",
    details: null,
  };
}

// { status, data }. 204·빈 본문이면 data = null
async function request(path, opts = {}) {
  let res;
  try {
    res = await fetch(API_BASE + path, {
      credentials: "include",
      ...opts,
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    });
  } catch {
    throw new ClassAdminApiError(0);
  }
  if (!res.ok) {
    const { message, code, field, details } = await readErrorBody(res);
    throw new ClassAdminApiError(res.status, message, code, field, details);
  }
  if (res.status === 204) return { status: 204, data: null };
  let text = "";
  try {
    text = await res.text();
  } catch {
    throw new ClassAdminApiError(res.status, "", "BAD_JSON");
  }
  if (!text) return { status: res.status, data: null };
  try {
    return { status: res.status, data: JSON.parse(text) };
  } catch {
    throw new ClassAdminApiError(res.status, "", "BAD_JSON");
  }
}

function seg(value) {
  return encodeURIComponent(String(value == null ? "" : value));
}

// ---------- 수업 ----------

// 수업 추가. 만든 수업(서버 응답 그대로)
export async function createClass(body) {
  const { data } = await request("/v1/admin/classes", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}

// 수업 수정(예약이 없는 수업만)
export async function updateClass(classId, body) {
  const { data } = await request(`/v1/admin/classes/${seg(classId)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return data;
}

// 수업 삭제(예약·운동 기록이 없는 수업만)
export async function deleteClass(classId) {
  await request(`/v1/admin/classes/${seg(classId)}`, { method: "DELETE" });
}

// 그 날짜 수업을 모두 지운다(CAR-130 날짜 닫기, 기본값 D2). 지운 수업 수, 응답에 0 이상의 정수 deleted가 없으면 null.
// 하나라도 예약 행(취소 포함)이나 운동 기록이 있으면 서버가 아무것도 지우지 않고 409(ClassAdminApiError, describeCloseDateError)
export async function closeClassDate(date) {
  const { data } = await request(
    `/v1/admin/classes?date=${encodeURIComponent(String(date || ""))}`,
    { method: "DELETE" }
  );
  const n = data && typeof data === "object" ? data.deleted : undefined;
  return Number.isInteger(n) && n >= 0 ? n : null;
}

// 그 수업에 운동 기록이 있는가. 모르면(오류·이상한 응답) null
export async function classHasWorkouts(classId) {
  try {
    const { data } = await request(
      `/v1/admin/classes/${seg(classId)}/workouts/exists`
    );
    return typeof data?.exists === "boolean" ? data.exists : null;
  } catch {
    return null;
  }
}

// 그 수업에 예약 행이 있는가(취소한 예약 포함, 서버 수정·삭제 잠금과 같은 기준). 모르면(오류·이상한 응답) null.
// 예약자 목록(GET …/bookings)은 확정 예약만 주므로 예약이 모두 취소된 수업은 이것으로만 잠금을 안다(R9 리뷰 1차)
export async function classHasBookingRows(classId) {
  try {
    const { data } = await request(
      `/v1/admin/classes/${seg(classId)}/bookings/exists`
    );
    return typeof data?.exists === "boolean" ? data.exists : null;
  } catch {
    return null;
  }
}

// 그 날짜 관리자 스케줄(수업 배열). 실패는 ClassAdminApiError
export async function fetchSchedule(date) {
  const { data } = await request(
    `/v1/admin/classes/schedule?date=${encodeURIComponent(String(date || ""))}`
  );
  return Array.isArray(data?.classes) ? data.classes : [];
}

// ---------- 운동 프로그램 ----------

// from~to(둘 다 포함) 프로그램 목록
export async function listDailyPrograms(from, to) {
  const params = new URLSearchParams({
    from: String(from || ""),
    to: String(to || ""),
  });
  const { data } = await request(`/v1/admin/daily-programs?${params}`);
  return Array.isArray(data?.items) ? data.items : [];
}

// 한 날짜·타입 프로그램. 아직 없으면(404) null
export async function getDailyProgram(date, type) {
  try {
    const { data } = await request(
      `/v1/admin/daily-programs/${seg(date)}/${seg(type)}`
    );
    return data && typeof data === "object" ? data : null;
  } catch (err) {
    if (err instanceof ClassAdminApiError && err.status === 404) return null;
    throw err;
  }
}

// 저장. { item, created, regenerated }: created = 201(이 날짜·타입을 처음 저장), regenerated = 서버가 이번 저장에서 다시 만든
// 오늘의 총평 수(summaries_regenerated, 0 이상의 정수가 아니면 null). 처음 저장이라도 그 날짜·타입 기록이 아직 없으면 0이다(R9 리뷰 4차)
export async function saveDailyProgram(date, type, body) {
  const { status, data } = await request(
    `/v1/admin/daily-programs/${seg(date)}/${seg(type)}`,
    { method: "PUT", body: JSON.stringify(body) }
  );
  const n =
    data && typeof data === "object" ? data.summaries_regenerated : undefined;
  return {
    item: data,
    created: status === 201,
    regenerated: Number.isInteger(n) && n >= 0 ? n : null,
  };
}

// ---------- 오류 문구 ----------

function isKorean(text) {
  return /[가-힣]/.test(String(text || ""));
}

function commonError(err, what) {
  const status = err?.status;
  if (status === 401) return "로그인이 만료됐어요. 다시 로그인해 주세요.";
  if (status === 403) return `관리자 계정만 ${what} 수 있어요.`;
  if (status === 0) {
    return "서버 응답을 받지 못했어요. 목록을 새로 불러와 확인한 뒤 다시 시도해 주세요.";
  }
  return "";
}

// 수업 추가·수정·삭제 실패 문구. action: "create" | "update" | "delete"
//   409 CLASS_HAS_BOOKINGS → 예약(취소한 예약 포함)이 있어 못 고침/못 지움, 409 CLASS_HAS_WORKOUTS → 운동 기록이 있어 못 고침/못 지움,
//   409 CLASS_TIME_CONFLICT → 같은 날짜·시작 시각에 다른 수업이 있음(서버 문구 끝 괄호에 그 수업 시각·타입 표시 이름, 기타는 수업 이름.
//   이름에 괄호가 들어 있어도 "(HH:MM " 부터 끝 괄호까지를 그 수업으로 읽는다),
//   코드를 모르는 409는 둘 다 적은 이유, 422 → 서버 문구(한국어일 때) 또는 칸에 맞는 입력 확인(이름 칸 title은 이름 규칙, 그 밖에는 타입),
//   404 → 이미 지워졌을 수 있음
export function describeClassError(err, action) {
  const status = err?.status;
  const code = String(err?.code || "");
  const verb =
    action === "create"
      ? "추가하지"
      : action === "delete"
      ? "삭제하지"
      : "고치지";
  if (status === 409) {
    if (code === "CLASS_TIME_CONFLICT") {
      const m = /\((\d{1,2}:\d{2}(?: [\s\S]*)?)\)\s*$/.exec(
        String(err?.serverMessage || "")
      );
      const other = m && isKorean(err.serverMessage) ? ` (${m[1]})` : "";
      return `같은 날짜·시작 시각에 다른 수업이 있어요${other}. 시작 시각을 바꿔 주세요.`;
    }
    const what = action === "delete" ? "삭제할" : "수정할";
    if (code === "CLASS_HAS_WORKOUTS") {
      return `운동 기록이 있는 수업은 ${what} 수 없어요.`;
    }
    if (code === "CLASS_HAS_BOOKINGS") {
      return `예약(취소한 예약 포함)이 있는 수업은 ${what} 수 없어요.`;
    }
    return `예약이나 운동 기록이 있는 수업은 ${what} 수 없어요.`;
  }
  if (status === 422 || status === 400) {
    if (isKorean(err?.serverMessage)) return err.serverMessage;
    if (err?.field === "title") {
      return "기타 수업 이름을 확인해 주세요. 20자까지, 줄바꿈·탭·**·== 없이, 다른 타입 이름과 다르게 써 주세요.";
    }
    return "입력한 값을 확인해 주세요. 타입은 9개 중에서 골라야 해요.";
  }
  if (status === 404) {
    return "수업을 찾을 수 없어요. 이미 삭제됐을 수 있어요.";
  }
  const common = commonError(err, "수업을 바꿀");
  if (common) return common;
  return `수업을 ${verb} 못했어요 (오류 ${status}). 잠시 뒤 다시 시도해 주세요.`;
}

// ---------- 날짜 닫기(CAR-130, 기본값 D2) 문구 ----------

// 날짜 닫기 409의 막힌 수업 목록: details의 classes·blocked(서버가 쓰는 키)·blocked_classes(없으면 처음 나오는 배열)를 읽어 "06:30 Pull & Cardio(예약)"
// 꼴의 글자 배열로 만든다. 항목이 글자면 그대로 쓴다. 객체면 시각(time·start_time, "HH:MM"까지)과 이름(display_name·name, 기타 수업은
// title, 그다음 type_label·title·type), 이유(reason·code에 booking이면 "예약", workout이면 "운동 기록")를 쓴다. 목록이 없으면 []
export function closeDateBlockedClasses(err) {
  const details =
    err && err.details && typeof err.details === "object" ? err.details : null;
  if (!details) return [];
  const known = ["classes", "blocked", "blocked_classes"]
    .map((k) => details[k])
    .find(Array.isArray);
  const list = known || Object.values(details).find(Array.isArray) || [];
  const out = [];
  for (const item of list) {
    if (typeof item === "string") {
      if (item.trim()) out.push(item.trim());
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const rawTime = String(item.time || item.start_time || "").trim();
    const time = /^\d{1,2}:\d{2}/.test(rawTime)
      ? rawTime.replace(/^(\d{1,2}:\d{2}).*$/, "$1").padStart(5, "0")
      : rawTime;
    const pick = (...values) =>
      values.find((v) => typeof v === "string" && v.trim());
    const name = pick(
      item.display_name,
      item.name,
      item.type === "other" ? item.title : "",
      item.type_label,
      item.title,
      item.type
    );
    const why = String(item.reason || item.code || "").toLowerCase();
    const reason = why.includes("workout")
      ? "운동 기록"
      : why.includes("booking")
      ? "예약"
      : "";
    const text = [time, name ? name.trim().split(/\s+/).join(" ") : ""]
      .filter(Boolean)
      .join(" ");
    if (text) out.push(reason ? `${text}(${reason})` : text);
  }
  return out;
}

// 날짜 닫기 409에 붙이는 이유(R9: 예약·기록이 있는 수업은 지우지 않는다, record-daily-design.md 0절 9·DECISIONS R9-5)
export const CLOSE_DATE_BLOCKED_RULE =
  "예약(취소한 예약 포함)이나 운동 기록이 있는 수업은 삭제할 수 없어서 이 날짜의 수업을 하나도 지우지 않았어요.";

// 날짜 닫기 실패 문구.
//   409 → "이 날짜를 닫지 못했어요." + 이유 + 막힌 수업(details 목록, 없으면 서버 문구가 한국어일 때 괄호로),
//   400·422 → 서버 문구(한국어일 때) 또는 날짜 확인, 404·405 → 서버가 아직 날짜 닫기를 모름(배포 전),
//   401·403·네트워크 → 공통 문구, 그 밖 → 오류 번호
export function describeCloseDateError(err) {
  const status = err?.status;
  const head = "이 날짜를 닫지 못했어요.";
  if (status === 409) {
    const names = closeDateBlockedClasses(err);
    if (names.length) {
      return `${head} ${CLOSE_DATE_BLOCKED_RULE} 막힌 수업: ${names.join(
        ", "
      )}`;
    }
    const msg = String(err?.serverMessage || "").trim();
    if (isKorean(msg)) return `${head} ${CLOSE_DATE_BLOCKED_RULE} (${msg})`;
    return `${head} ${CLOSE_DATE_BLOCKED_RULE}`;
  }
  if (status === 400 || status === 422) {
    if (isKorean(err?.serverMessage)) return `${head} ${err.serverMessage}`;
    return `${head} 날짜를 확인해 주세요.`;
  }
  if (status === 404 || status === 405) {
    return "서버가 아직 날짜 닫기를 지원하지 않아요. 서버를 새로 배포한 뒤 다시 시도하거나 수업을 하나씩 삭제해 주세요.";
  }
  const common = commonError(err, "수업을 바꿀");
  if (common) return common;
  return `이 날짜를 닫지 못했어요 (오류 ${status}). 잠시 뒤 다시 시도해 주세요.`;
}

// 그 날짜 수업(스케줄)을 불러오지 못했을 때 문구(무료체험 신청 편집 창의 시간 선택지)
export function describeScheduleLoadError(err) {
  const status = err?.status;
  if (status === 401) return "로그인이 만료됐어요. 다시 로그인해 주세요.";
  if (status === 403) return "관리자 계정만 수업 시간을 볼 수 있어요.";
  if (status === 0) {
    return "서버에 연결하지 못해 이 날짜의 수업 시간을 불러오지 못했어요. 날짜를 다시 골라 주세요.";
  }
  return `이 날짜의 수업 시간을 불러오지 못했어요 (오류 ${status}). 날짜를 다시 골라 주세요.`;
}

// 운동 프로그램 저장 실패 문구
export function describeProgramSaveError(err) {
  const status = err?.status;
  if (status === 422 || status === 400) {
    return isKorean(err?.serverMessage)
      ? err.serverMessage
      : "입력한 값을 확인해 주세요. 안내 문장은 꼭 써야 해요.";
  }
  const common = commonError(err, "운동 프로그램을 저장할");
  if (common) return common;
  return `저장하지 못했어요 (오류 ${status}). 잠시 뒤 다시 시도해 주세요.`;
}

// 운동 프로그램 불러오기 실패 문구
export function describeProgramLoadError(err) {
  const status = err?.status;
  if (status === 401) return "로그인이 만료됐어요. 다시 로그인해 주세요.";
  if (status === 403) return "관리자 계정만 운동 프로그램을 볼 수 있어요.";
  if (status === 0) {
    return "서버에 연결하지 못했어요. 잠시 뒤 다시 불러와 주세요.";
  }
  return `운동 프로그램을 불러오지 못했어요 (오류 ${status}).`;
}
